import { create } from 'zustand'

export type CountdownMeta = {
  courseName?: string
  encodedOrderId?: string
}

export type CountdownItem = CountdownMeta & {
  orderId: string
  expireAtMs: number
  remainingSeconds: number
}

type PersistedCountdown = {
  orderId: string
  expireAtMs: number
  meta?: CountdownMeta
}

type OrderCountdownState = {
  countdowns: Record<string, CountdownItem>
  upsertCountdown: (params: {
    orderId: string
    expireAtMs: number
    meta?: CountdownMeta
  }) => void
  removeCountdown: (orderId: string) => void
  tick: () => void
}

const STORAGE_KEY = 'gk-order-countdowns'
const isBrowser = typeof window !== 'undefined' // avoid touching window/localStorage during SSR/tests

const computeRemainingSeconds = (expireAtMs: number) => {
  return Math.max(0, Math.floor((expireAtMs - Date.now()) / 1000))
}

const serializeCountdowns = (countdowns: Record<string, CountdownItem>) => {
  if (!isBrowser) return
  const payload: Record<string, PersistedCountdown> = {}
  Object.values(countdowns).forEach(({ orderId, expireAtMs, courseName, encodedOrderId }) => {
    payload[orderId] = {
      orderId,
      expireAtMs,
      meta: {
        courseName,
        encodedOrderId,
      },
    }
  })
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (error) {
    // ignore quota errors
  }
}

const loadCountdowns = (): Record<string, CountdownItem> => {
  if (!isBrowser) return {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const persisted: Record<string, PersistedCountdown> = JSON.parse(raw)
    const now = Date.now()
    return Object.values(persisted).reduce<Record<string, CountdownItem>>(
      (acc, item) => {
        if (!item || typeof item.expireAtMs !== 'number') {
          return acc
        }
        if (item.expireAtMs <= now) {
          return acc
        }
        const remainingSeconds = computeRemainingSeconds(item.expireAtMs)
        acc[item.orderId] = {
          orderId: item.orderId,
          expireAtMs: item.expireAtMs,
          remainingSeconds,
          ...(item.meta || {}),
        }
        return acc
      },
      {},
    )
  } catch (error) {
    return {}
  }
}

export const useOrderCountdownStore = create<OrderCountdownState>((set) => ({
  countdowns: loadCountdowns(),
  upsertCountdown: ({ orderId, expireAtMs, meta }) =>
    set((state) => {
      const remainingSeconds = computeRemainingSeconds(expireAtMs)
      const nextCountdowns = {
        ...state.countdowns,
        [orderId]: {
          orderId,
          expireAtMs,
          remainingSeconds,
          ...meta,
        },
      }
      serializeCountdowns(nextCountdowns)
      return {
        countdowns: nextCountdowns,
      }
    }),
  removeCountdown: (orderId) =>
    set((state) => {
      if (!state.countdowns[orderId]) return state
      const next = { ...state.countdowns }
      delete next[orderId]
      serializeCountdowns(next)
      return {
        countdowns: next,
      }
    }),
  tick: () => {
    const now = Date.now()
    const expiredIds: string[] = []
    set((state) => {
      const entries = Object.entries(state.countdowns)
      if (entries.length === 0) return state

      let hasChanges = false
      const nextCountdowns: Record<string, CountdownItem> = {}

      entries.forEach(([orderId, countdown]) => {
        const diffMs = countdown.expireAtMs - now
        const remainingSeconds = Math.max(0, Math.floor(diffMs / 1000))
        if (remainingSeconds !== countdown.remainingSeconds) {
          hasChanges = true
        }
        if (diffMs <= 0) {
          expiredIds.push(orderId)
        }
        nextCountdowns[orderId] = {
          ...countdown,
          remainingSeconds,
        }
      })

      if (!hasChanges) return state

      return {
        countdowns: nextCountdowns,
      }
    })

    if (expiredIds.length) {
      set((state) => {
        const next = { ...state.countdowns }
        let removed = false
        expiredIds.forEach((orderId) => {
          const countdown = next[orderId]
          if (countdown && countdown.remainingSeconds <= 0) {
            delete next[orderId]
            removed = true
          }
        })
        if (!removed) return state
        serializeCountdowns(next)
        return {
          countdowns: next,
        }
      })
    }
  },
}))
